import { useEffect } from 'react'
import { Card } from '../common/Card'
import type { NewlyEarnedBadge, BadgeRarity } from '../../types'

interface BadgeEarnedToastProps {
  badge: NewlyEarnedBadge
  onClose: () => void
  onViewDetails?: () => void
}

const rarityGradients: Record<BadgeRarity, string> = {
  common: 'from-gray-200 to-gray-300',
  uncommon: 'from-green-200 to-green-400',
  rare: 'from-blue-200 to-blue-400',
  epic: 'from-purple-200 to-purple-400',
  legendary: 'from-primary-300 to-primary-500',
}

export function BadgeEarnedToast({ badge, onClose, onViewDetails }: BadgeEarnedToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full px-4 animate-slide-down">
      <Card padding="md" className="shadow-xl border-2 border-primary-200">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-full bg-gradient-to-br ${
              rarityGradients[badge.rarity]
            } flex items-center justify-center animate-bounce-slow shadow-md`}
          >
            {badge.icon_url ? (
              <img src={badge.icon_url} alt="" className="w-8 h-8" />
            ) : (
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-primary-600 font-medium">Badge Earned!</p>
            <p className="font-bold text-brown-900 truncate">{badge.name}</p>
          </div>
          {onViewDetails && (
            <button
              onClick={onViewDetails}
              className="text-primary-600 text-sm font-medium hover:text-primary-700"
            >
              View
            </button>
          )}
          <button
            onClick={onClose}
            className="text-brown-400 hover:text-brown-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </Card>
    </div>
  )
}
